import {
  collection,
  doc,
  getDocs,
  getDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
  addDoc
} from 'firebase/firestore';
import { db } from './config';

const COLLECTION = 'events';
const NOTIFICATIONS_COLLECTION = 'notifications';

export const eventService = {
  /**
   * Get all upcoming public events
   * Only returns events that are open for registration
   */
  async getUpcoming(limit = 20) {
    try {
      const now = new Date();
      const q = query(
        collection(db, COLLECTION),
        where('date', '>=', Timestamp.fromDate(now)),
        orderBy('date', 'asc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs
        .slice(0, limit)
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate?.() || doc.data().date
        }))
        .filter(event => event.status !== 'closed'); // Only show open/filling-fast events
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
      return [];
    }
  },

  /**
   * Get event by ID
   */
  async getById(id) {
    try {
      const docRef = doc(db, COLLECTION, id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          date: data.date?.toDate?.() || data.date
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching event:', error);
      return null;
    }
  },

  /**
   * Get events by category
   */
  async getByCategory(category, limit = 10) {
    try {
      const now = new Date();
      const q = query(
        collection(db, COLLECTION),
        where('category', '==', category),
        where('date', '>=', Timestamp.fromDate(now)),
        orderBy('date', 'asc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs
        .slice(0, limit)
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate?.() || doc.data().date
        }))
        .filter(event => event.status !== 'closed');
    } catch (error) {
      console.error('Error fetching events by category:', error);
      return [];
    }
  },

  /**
   * Register an attendee for an event
   * Updates the event with the new attendee and decrements available spots
   */
  async registerAttendee(eventId, attendeeData) {
    try {
      const event = await this.getById(eventId);
      if (!event) {
        throw new Error('Event not found');
      }
      if (event.spots.available <= 0) {
        throw new Error('Event is full');
      }

      // Check if email already registered
      const existingAttendee = (event.attendees || []).find(
        a => a.email?.toLowerCase() === attendeeData.email?.toLowerCase()
      );
      if (existingAttendee) {
        throw new Error('You are already registered for this event');
      }

      const docRef = doc(db, COLLECTION, eventId);
      const newAttendee = {
        name: attendeeData.name,
        email: attendeeData.email?.toLowerCase(),
        phone: attendeeData.phone || '',
        notes: attendeeData.notes || '',
        registeredAt: new Date().toISOString(),
        source: 'website'
      };

      const newAttendees = [...(event.attendees || []), newAttendee];
      const newAvailable = event.spots.available - 1;

      // Determine new status
      let newStatus = 'open';
      if (newAvailable <= 0) {
        newStatus = 'full';
      } else if (newAvailable <= 3) {
        newStatus = 'filling-fast';
      }

      await updateDoc(docRef, {
        attendees: newAttendees,
        spots: {
          ...event.spots,
          available: newAvailable
        },
        status: newStatus,
        updatedAt: serverTimestamp()
      });

      // Create notification for admin
      await this.createRegistrationNotification(event, newAttendee);

      return {
        id: eventId,
        attendee: newAttendee,
        spotsRemaining: newAvailable
      };
    } catch (error) {
      console.error('Error registering attendee:', error);
      throw error;
    }
  },

  /**
   * Create a notification for admin when someone registers
   */
  async createRegistrationNotification(event, attendee) {
    try {
      const notificationData = {
        type: 'event_registration',
        title: 'New Event Registration',
        message: `${attendee.name} registered for ${event.title}`,
        eventId: event.id,
        eventTitle: event.title,
        attendeeName: attendee.name,
        attendeeEmail: attendee.email,
        attendeePhone: attendee.phone,
        eventDate: event.date,
        eventTime: event.time,
        spotsRemaining: event.spots.available - 1,
        read: false,
        readBy: [],
        createdAt: serverTimestamp()
      };
      await addDoc(collection(db, NOTIFICATIONS_COLLECTION), notificationData);
    } catch (err) {
      console.error('Error creating registration notification:', err);
    }
  },

  /**
   * Get past events (events that have already happened)
   */
  async getPast(limit = 20) {
    try {
      const now = new Date();
      const q = query(
        collection(db, COLLECTION),
        where('date', '<', Timestamp.fromDate(now)),
        orderBy('date', 'desc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs
        .slice(0, limit)
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate?.() || doc.data().date
        }));
    } catch (error) {
      console.error('Error fetching past events:', error);
      return [];
    }
  },

  /**
   * Check if an event has available spots
   */
  async hasAvailableSpots(eventId) {
    try {
      const event = await this.getById(eventId);
      return event && event.spots.available > 0;
    } catch (error) {
      console.error('Error checking event availability:', error);
      return false;
    }
  },

  /**
   * Get spots information for an event
   */
  async getSpots(eventId) {
    try {
      const event = await this.getById(eventId);
      if (!event) return null;
      return {
        total: event.spots.total,
        available: event.spots.available,
        taken: event.spots.total - event.spots.available,
        status: event.status
      };
    } catch (error) {
      console.error('Error getting event spots:', error);
      return null;
    }
  }
};

export default eventService;
